<template>
  <div class="wiki-search-bar">
    <AutoComplete
      v-model="query"
      :suggestions="suggestions"
      optionLabel="label"
      :forceSelection="false"
      placeholder="ابحث عن مقالات، وسوم أو موظفين (#وسم أو @اسم)"
      :loading="loading"
      @complete="handleComplete"
      @item-select="handleSelect"
      @keyup.enter="emitSearch"
    >
      <template #option="{ option }">
        <div class="suggestion">
          <span class="suggestion-type">{{ option.typeLabel }}</span>
          <span>{{ option.label }}</span>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AutoComplete from 'primevue/autocomplete'
import { searchWikiSuggestions } from '@/services/wiki'

type SuggestionOption = {
  label: string
  type: 'article' | 'tag' | 'employee'
  typeLabel: string
  value: any
}

const emit = defineEmits<{
  search: [value: string]
  select: [value: { type: string; value: any }]
}>()

const query = ref('')
const suggestions = ref<SuggestionOption[]>([])
const loading = ref(false)
let lastFetch = 0

async function handleComplete(event: { query: string }) {
  const text = event.query?.trim()
  emit('search', text || '')
  if (!text) {
    suggestions.value = []
    return
  }
  const now = Date.now()
  if (now - lastFetch < 150) return
  lastFetch = now
  loading.value = true
  try {
    const data = await searchWikiSuggestions(text)
    const options: SuggestionOption[] = [
      ...data.tags.map((tag) => ({
        label: `#${tag.label}`,
        type: 'tag' as const,
        typeLabel: 'وسم',
        value: tag,
      })),
      ...data.articles.map((article) => ({
        label: article.title,
        type: 'article' as const,
        typeLabel: 'مقال',
        value: article,
      })),
      ...data.employees.map((emp) => ({
        label: `@${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}`.trim(),
        type: 'employee' as const,
        typeLabel: 'موظف',
        value: emp,
      })),
    ]
    suggestions.value = options
  } catch (err) {
    console.error('Wiki search failed', err)
  } finally {
    loading.value = false
  }
}

function handleSelect(option: { value: SuggestionOption }) {
  if (!option?.value) return
  emit('select', option.value)
}

function emitSearch() {
  emit('search', query.value.trim())
}
</script>

<style scoped>
.wiki-search-bar {
  width: 100%;
}

.suggestion {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.suggestion-type {
  font-size: 0.75rem;
  color: #64748b;
  background: #e2e8f0;
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
}
</style>
